/**
 * Unified API error message extractor.
 *
 * Handles two error shapes:
 *  - RTK Query (.unwrap() rejects with): { status, data: { error, errors? } }
 *  - Axios service calls reject with:    { response: { data: { message, errors? } }, message }
 *
 * If the server returned field-level validation errors they are formatted as:
 *   "Field name: message • Other field: message"
 */

function formatValidationErrors(errors: Record<string, string | string[]>): string {
  return Object.entries(errors)
    .map(([field, msgs]) => {
      // camelCase → "Camel Case"
      const label = field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase());
      const text = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
      return `${label}: ${text}`;
    })
    .join(' • ');
}

export function getApiError(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  if (!error) return fallback;
  const err = error as any;

  // ── RTK Query errors ──────────────────────────────────────────────────────
  if (err?.data?.errors && typeof err.data.errors === 'object') {
    return formatValidationErrors(err.data.errors as Record<string, string | string[]>);
  }
  if (err?.data?.code && typeof err.data.code === 'string') {
    const code = String(err.data.code);
    if (code === 'AI_RATE_LIMITED' || code === 'RATE_LIMITED') {
      const retry = err.data.retryAfterSeconds;
      return retry
        ? `Too many AI requests — try again in ${retry} seconds.`
        : 'Too many AI requests — wait a minute and try again.';
    }
  }
  if (err?.data?.code === 'STORAGE_QUOTA_EXCEEDED') {
    const base = String(err.data.error || fallback);
    const suggested = err.data.suggestedPlan as string | undefined;
    if (suggested && !base.toLowerCase().includes(String(suggested).toLowerCase())) {
      return `${base} Consider upgrading to ${suggested}.`;
    }
    return base;
  }
  if (err?.data?.error) return String(err.data.error);
  if (err?.data?.message) return String(err.data.message);

  // ── Axios / fetch service errors ──────────────────────────────────────────
  if (err?.response?.data?.errors && typeof err.response.data.errors === 'object') {
    return formatValidationErrors(
      err.response.data.errors as Record<string, string | string[]>,
    );
  }
  if (err?.response?.data?.message) return String(err.response.data.message);
  if (err?.response?.data?.error) return String(err.response.data.error);

  // ── Plain Error / string ──────────────────────────────────────────────────
  if (err?.message && typeof err.message === 'string') return err.message;
  if (typeof err === 'string' && err.trim()) return err;

  return fallback;
}
